<template>
  <v-dialog
    v-model="dialog"
    max-width="400"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <v-card v-if="agreement">
      <template v-if="$vuetify.breakpoint.smAndUp">
        <v-toolbar elevation="0" dense dark class="red">
          <v-spacer></v-spacer>

          <v-btn icon @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-title class="headline white--text pt-0 red" style="max-height: 48px">{{
          $t('message.request_stop')
        }}</v-card-title>
      </template>

      <v-toolbar v-else elevation="0" dark class="red" style="max-height: 54px;">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('message.request_stop') }}</v-toolbar-title>
      </v-toolbar>

      <v-card-text class="pt-5">
        <v-row v-if="agreementLoading" class="fill-height" align="center" justify="center" style="min-height: 200px">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-row>
        <template v-else-if="agreement">
          <template v-if="startDate && agreement.remainingDeactivationDays[new Date(startDate).getFullYear()] === 0">
            <div class="title font-weight-regular text-center my-5">
              {{
                $t('message.you_do_not_have_any_stop_day_remaining', {
                  year: new Date(startDate).getFullYear()
                })
              }}
            </div>
          </template>
          <template v-else>
            <v-overlay :value="periodLoading" :absolute="true" :dark="false" color="white" opacity="0.90">
              <div class="text-center pa-5">
                <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
                <div>{{ $t('message.request_being_processed') }}</div>
              </div>
            </v-overlay>

            <v-alert
              v-if="startDate || agreement.networkPlatform.scheduleAnticipation > 0"
              type="info"
              :value="true"
              class="mb-5"
            >
              <div v-if="agreement.networkPlatform.scheduleAnticipation > 0" class="mb-4">
                {{
                  $tc('message.stop_schedule_anticipation_info', agreement.networkPlatform.scheduleAnticipation, {
                    date: $moment(minStartDate).format('LL')
                  })
                }}
              </div>

              <div v-if="startDate">
                {{
                  $t('message.you_have_days_of_stop_to_use', {
                    days: agreement.remainingDeactivationDays[new Date(startDate).getFullYear()],
                    year: new Date(startDate).getFullYear()
                  })
                }}
              </div>
            </v-alert>

            <v-alert v-if="overlapError" :value="true" color="error" icon="warning" class="mb-5" dark>{{
              $t('message.' + overlapError + '_overlap_error')
            }}</v-alert>
            <v-alert
              v-if="periodCreateError && !periodViolations"
              :value="true"
              color="error"
              icon="warning"
              class="mb-5"
              dark
            >
              {{ $t('error.' + toSnakeCase(periodCreateError)) }}
            </v-alert>
            <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit(item)">
              <v-label>{{ $t('message.select_stop_days') }}</v-label>

              <v-menu v-model="startDateMenu" :close-on-content-click="false" max-width="290">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formattedStartDate"
                    :label="$t('message.start_date')"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDate"
                  :locale="$i18n.locale"
                  :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
                  :min="minStartDate"
                  :max="maxStartDate"
                  :allowed-dates="allowedDates"
                  @change="startDateMenu = false"
                ></v-date-picker>
              </v-menu>

              <v-menu v-if="!noEndDate" v-model="endDateMenu" :close-on-content-click="false" max-width="290">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="formattedEndDate"
                    :label="$t('message.end_date')"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDate"
                  :locale="$i18n.locale"
                  :first-day-of-week="'es' === $i18n.locale ? 1 : 0"
                  :min="minEndDate"
                  :max="maxEndDate"
                  :allowed-dates="allowedDates"
                  @change="endDateMenu = false"
                ></v-date-picker>
              </v-menu>

              <v-checkbox v-model="noEndDate" :label="$t('message.no_end_date')"></v-checkbox>

              <v-alert dark :value="!!discount" type="info" class="mb-3"
                >Con esta parada se añadira un descuento de {{ discount }}€ en tu próxima factura.</v-alert
              >

              <v-btn color="orange" class="white--text" type="submit">{{ $t('message.request_stop') }}</v-btn>
            </v-form>
          </template>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import fetch from '@/utils/fetch'
import { toSnakeCase } from '@/utils/string'

export default {
  name: 'StopCreateDialog',
  props: {
    periods: {
      type: Array,
      required: false,
      default: null
    },
    agreementId: {
      type: String,
      required: true
    },
    startsAt: {
      type: Date,
      required: false,
      default: null
    },
    endsAt: {
      type: Date,
      required: false,
      default: null
    },
    reset: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      unavailableDays: [],
      item: {
        startsAt: null,
        endsAt: null,
        airtimePlan: null,
        pricingDescription: '',
        type: 'stop'
      },
      agreement: null,
      agreementLoading: false,
      valid: true,
      startDateMenu: false,
      endDateMenu: false,
      startDate: this.$moment().format('YYYY-MM-DD'),
      endDate: this.$moment()
        .add(1, 'day')
        .format('YYYY-MM-DD'),
      minEndDate: this.$moment()
        .toISOString()
        .substr(0, 10),
      overlapError: null,
      noEndDate: false,
      toSnakeCase: toSnakeCase
    }
  },
  computed: {
    ...mapGetters({
      periodCreated: 'serviceperiod/create/created',
      periodLoading: 'serviceperiod/create/isLoading',
      periodCreateError: 'serviceperiod/create/error',
      periodViolations: 'serviceperiod/create/violations'
    }),

    dialog: {
      get: function() {
        return this.show
      },
      set: function(newValue) {
        if (newValue === false) {
          this.$emit('close')
        }
        return this.show
      }
    },

    minStartDate() {
      if (!this.agreement) {
        return
      }
      let date = this.$moment()
        .tz('Europe/Madrid')
        .add(this.agreement.networkPlatform.scheduleAnticipation, 'days')

      if (
        this.$moment()
          .tz('Europe/Madrid')
          .hour() >= 8
      ) {
        date = date.add(1, 'days')
      }

      return date.toISOString().substr(0, 10)
    },

    maxStartDate() {
      let maxStartDate = this.$moment()
        .add(1, 'years')
        .toISOString()
        .substr(0, 10)

      if (this.agreement && this.agreement.endsAt) {
        maxStartDate = this.$moment(this.agreement.endsAt)
          .toISOString()
          .substr(0, 10)
      }

      return maxStartDate
    },

    maxEndDate() {
      let today = new Date()
      let year = today.getFullYear()
      if (!this.agreement.remainingDeactivationDays || 'swap' === this.item.type) {
        return
      }
      return this.$moment(this.startDate)
        .add(this.agreement.remainingDeactivationDays[year], 'day')
        .subtract(1, 'day')
        .format('YYYY-MM-DD')
    },

    formattedStartDate() {
      if (!this.startDate) {
        return this.$moment().format('YYYY-MM-DD')
      }

      return this.$moment(this.startDate).format('YYYY-MM-DD')
    },

    formattedEndDate() {
      if (!this.startDate) {
        return this.$moment()
          .add(1, 'day')
          .format('YYYY-MM-DD')
      }

      if (!this.endDate) {
        return this.$moment(this.startDate)
          .add(1, 'day')
          .format('YYYY-MM-DD')
      }

      if (this.endDate < this.startDate) {
        return this.$moment(this.startDate)
          .add(1, 'day')
          .format('YYYY-MM-DD')
      }
      return this.$moment(this.endDate).format('YYYY-MM-DD')
    },

    itemComplete() {
      return this.item.startsAt && this.item.endsAt && this.item.airtimePlan
    },

    discount() {
      if (this.activePrice && this.stoppedPrice) {
        return this.activePrice - this.stoppedPrice
      }
      return null
    }
  },

  watch: {
    reset: function(reset) {
      if (reset) {
        this.resetPeriod()
      }
    },

    periodViolations: function(periodViolations) {
      if (periodViolations && 'date' in periodViolations && periodViolations.date.search('overlaps') > 0) {
        if (!this.item.endsAt) {
          this.overlapError = 'stop_later'
        } else if (periodViolations.date.search('-  for') > 0) {
          this.overlapError = 'stop_indefinite_earlier'
        } else {
          this.overlapError = 'stop'
        }
      }
    },

    itemComplete: function(itemComplete) {
      if (!itemComplete) {
        return
      }
    },

    dialog() {
      if (this.dialog) {
        this.fetchServiceAgreement(this.agreementId)
        this.startDate = null
        this.endDate = null
      }
    },
    startDate: function(startDate) {
      if (!startDate) {
        this.item.startsAt = null
        this.activePrice = null
        this.stoppedPrice = null
        return
      }

      this.minEndDate = startDate

      if (startDate > this.endDate) {
        this.endDate = null
      }

      this.updateStartDate()
      this.updateDiscount()
    },

    endDate: function(endDate) {
      if (!endDate) {
        this.item.endsAt = null
        this.activePrice = null
        this.stoppedPrice = null
        return
      }

      this.updateEndDate()
      this.updateDiscount()
    },

    periodCreated: function(periodCreated) {
      if (!periodCreated) {
        return
      }

      this.$emit('periodCreated', periodCreated)
      this.resetPeriod()
    },

    startsAt: function(startsAt) {
      if (!startsAt) {
        this.item.startsAt = null
        return
      }

      startsAt = new Date(startsAt.setHours(15))
      this.startDate = startsAt.toISOString().substr(0, 10)
    },

    endsAt: function(endsAt) {
      if (!endsAt) {
        this.item.endsAt = null
        return
      }

      endsAt = new Date(endsAt.setHours(15))
      this.endDate = endsAt.toISOString().substr(0, 10)
    },

    noEndDate: function(noEndDate) {
      if (noEndDate) {
        this.endDate = null
      }
    }
  },

  created() {
    this.updateStartDate()
    this.updateEndDate()
  },

  mounted() {
    this.fetchServiceAgreement(this.agreementId)
    this.item.agreement = this.agreementId
    this.getUnavailableDays(this.periods)
    this.startDate = null
    this.endDate = null
  },

  beforeDestroy() {
    this.resetPeriod()
  },

  methods: {
    ...mapActions({
      createPeriod: 'serviceperiod/create/create',
      resetPeriod: 'serviceperiod/create/reset'
    }),

    allowedDates(val) {
      let usedDates = []
      let compareDate = new Date(val).setHours(0, 0, 0, 0)
      let endsAt = null

      this.periods.forEach(servicePeriod => {
        let startsAt = new Date(servicePeriod.startsAt).setHours(0, 0, 0, 0)

        if (servicePeriod.endsAt) {
          endsAt = new Date(servicePeriod.endsAt).setHours(0, 0, 0, 0)
        }

        if (compareDate >= startsAt && endsAt === null) {
          usedDates.push(val)
        } else if (compareDate >= startsAt && compareDate < endsAt) {
          usedDates.push(val)
        }
      })
      return usedDates.indexOf(val)
    },
    getUnavailableDays(periods) {
      periods.forEach(period => {
        let startDay = this.$moment(period.startsAt)
        let endDay = this.$moment(period.endsAt)

        while (startDay.isBefore(endDay)) {
          this.unavailableDays.push(this.$moment(startDay).format('YYYY-MM-DD'))
          startDay.add(1, 'days')
        }
      })
    },
    fetchServiceAgreement: function(agreementId) {
      this.agreement = null
      this.agreementLoading = true
      fetch(agreementId)
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.agreement = data
          this.agreementLoading = false
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.agreementLoading = false
        })
    },

    updateStartDate() {
      if (!this.startDate) {
        return
      }
      this.item.startsAt = new Date(this.startDate).toISOString().substr(0, 10) + ' 08:00:00'
      this.overlapError = null
    },

    updateEndDate() {
      if (!this.endDate) {
        return
      }
      this.item.endsAt = new Date(this.endDate).toISOString().substr(0, 10) + ' 08:00:00'
      this.overlapError = null
    },

    updateDiscount() {
      if (this.agreement && this.item.startsAt && this.item.endsAt) {
        this.fetchActivePrice()
        this.fetchStoppedPrice()
      }
    },

    fetchActivePrice() {
      const startsAt = this.$moment(this.item.startsAt)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISOString()
      const endsAt = this.$moment(this.item.endsAt)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add('1', 'days')
        .toISOString()

      const headers = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json'
      })

      let url = this.agreement['@id'] + '/active_price?startsAt=' + startsAt + '&endsAt=' + endsAt
      fetch(url, { headers: headers })
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.activePrice = data.price
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    fetchStoppedPrice() {
      const startsAt = this.$moment(this.item.startsAt)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toISOString()
      const endsAt = this.$moment(this.item.endsAt)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add('1', 'days')
        .toISOString()

      const headers = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json'
      })

      let url = this.agreement['@id'] + '/stopped_price?startsAt=' + startsAt + '&endsAt=' + endsAt
      fetch(url, { headers: headers })
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.stoppedPrice = data.price
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    onSubmit(item) {
      if (this.$refs.form.validate()) {
        let cleanItem = { ...item }

        if (cleanItem.startsAt) {
          cleanItem.startsAt = this.$moment(cleanItem.startsAt)
            .tz('Europe/Madrid')
            .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        }

        if (cleanItem.endsAt) {
          cleanItem.endsAt = this.$moment(cleanItem.endsAt)
            .add(1, 'days')
            .tz('Europe/Madrid')
            .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        }

        if (cleanItem.airtimePlan && 'string' !== typeof cleanItem.airtimePlan) {
          cleanItem.airtimePlan = cleanItem.airtimePlan['@id']
        }

        if (cleanItem.orderLines && cleanItem.orderLines.length > 0) {
          cleanItem.orderLines.forEach((orderLine, index) => {
            if ('string' !== typeof orderLine) {
              cleanItem.orderLines[index] = orderLine['@id']
            }
          })
        }

        this.createPeriod(cleanItem)
      }
    },

    resetItem() {
      this.item = {
        startsAt: null,
        endsAt: null,
        airtimePlan: null,
        pricingDescription: ''
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.hidden {
  display: none;
}
</style>
